import { ViewType, ViewObject } from '../base';

import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';
import { TicketOffer } from '../objects/ticketoffer';
import { TicketOption } from '../objects/ticketoption';

import { TicketView } from './ticket';
import { OfferView } from './offer';
import { TicketProductView } from './ticketproduct';
import { ProductOptView } from './productopt';

export interface TicketOfferInfo {
    price: number,          // this is the total offer price with the included products
    total: number | null,   // this is the total products price without apllying the offer
    fixed: number | null,   // this is the aplied fixed price

    charge: number,         // total amount to be charged for the ticket offer
    refund: number          // total amount to be refunded for the ticket offer
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TicketOfferView extends ViewObject<TicketOffer> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get ticketoffer(): TicketOffer{
        return this.object;
    }
             
    constructor(object: TicketOffer, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _calcchargeinfo: TicketOfferInfo | null = null;
    private _calcofferinfo: { price: number | null, charge: number | null, refund: number | null } | null = null;
    private _calcpriceinfo: PriceType | null = null;     
    private _cartproducts: Array <TicketProductView> | null = null;
    private _validproducts: Array <TicketProductView> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('TICKETOFFER')){
            this._calcchargeinfo = null;
            this._calcofferinfo = null;
            this._calcpriceinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETPRODUCT')){
            this._cartproducts = null;
            this._validproducts = null;
            this._calcchargeinfo = null;
            this._calcofferinfo = null;
            this._calcpriceinfo = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];
        
        // register to updates on contained items  
        this._subscriptions.push(
            this.ticketoffer.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        )    
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/   

    get ticket(): TicketView | null {
        return this.alives.get(this.ticketoffer.ticket) as TicketView;
    }
    
    get offer(): OfferView | null {
        return this.alives.get(this.ticketoffer.offer) as OfferView;
    }

    get products(): Array <TicketProductView> {
        if (this._cartproducts === null){
            const _products: Array <TicketProductView> = [];

            for (const _product of this.ticketoffer.products){
                if (_product.InCart){
                    const _productview = this.alives.get(_product) as TicketProductView;
                    if (_productview){
                        _products.push(_productview);                        
                    }
                }
            }

            this._cartproducts = _products;
        }

        return this._cartproducts;
    }

    /**********************************/
    /* TICKET OFFER PRICE             */
    /**********************************/

    /* Only for display purposes: The ticket price is based on the products final price */
    get priceinfo(): PriceType {
        if (this._calcpriceinfo === null){
            this._calcpriceinfo = new PriceInfo(this.calcinfo.charge || 0).price;
        }

        return this._calcpriceinfo;
    }    

    get calcinfo(): TicketOfferInfo {
        if (this._calcchargeinfo === null){
            const _offerprice: number | null = this.__offerprice;  // ticketoffer catalog price
            const _offertotal: number | null = this.__totalprice   // ticketoffer products original price
            const _offerfixed: number | null = this.__fixedprice;  // ticketoffer manually fixed price
            const _rfundprice: number | null = this.__refndprice;  // refund price for this offer products 
            const _finalprice: number | null = this.__finalprice;   // final price for this offer products
            const _rawcharge = (_offerfixed != null) ? _offerfixed : (_finalprice || _offerprice || null);

            this._calcofferinfo = {
                price: _offerprice,
                charge: _rawcharge,
                refund: _rfundprice
            };

            this._calcchargeinfo = {
                price: _offerprice || 0,
                total: _offertotal,
                fixed: _offerfixed,

                charge: _rawcharge || 0,
                refund: _rfundprice || 0
            }
        }

        return this._calcchargeinfo;
    }    

    /**
     * Stores the calculated price, charge and refund on the ticket offer.
     * Reserved for the ticket recalculation flow: reading `calcinfo` never
     * leaves the offer pending commit.
     */
    Recalculate(): void {
        const _calcchargeinfo = this.calcinfo;
        const _calcofferinfo = this._calcofferinfo;
        if (!_calcofferinfo){
            return;
        }

        this.ticketoffer.price = _calcofferinfo.price;
        this.ticketoffer.charge = _calcofferinfo.charge;
        this.ticketoffer.refund = _calcofferinfo.refund;

        this._calcchargeinfo = _calcchargeinfo;      // the writes above invalidate the cache
        this._calcofferinfo = _calcofferinfo;
    }

    /**********************************/
    /* GROUPING FOR DISPLAY           */
    /**********************************/

    private _display_to: TicketOfferView | null = null;   // this ticketoffer is displayed in _display_to
    private _display_fr: TicketOfferView | null = null;   // this ticketoffer is displaying _display_fr
    
    get Display(): boolean {
        return (this._display_to == null);
    }

    get DisplayTo(): TicketOfferView | null {
        return this._display_to;
    }

    set DisplayTo(value: TicketOfferView | null){
        const _prev = this._display_to;
        if (_prev){
            _prev.DisplayFr = null;
        }

        const _next = value;
        if (_next){
            _next.DisplayFr = this.alives.get(this.ticketoffer) as TicketOfferView;
        }

        this._display_to = _next;
        this.DoRefreshView();
    }

    get DisplayFr(): TicketOfferView | null {
        return this._display_fr;
    }

    set DisplayFr(value: TicketOfferView | null){
        this._display_fr = value;
        this.DoRefreshView();
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/   

    AppliesTo(ticketoption: TicketOption): boolean {
        if (!this.offer){
            return false;
        }

        for(const product of this.offer.products){
            for (const option of product.options){
                if (option.IsValid && (option.option == ticketoption.option)){
                    return true;
                }
            }
        }

        return false;   // not found
    }

    IsGroup(ticketoffer: TicketOfferView): boolean{
        if (ticketoffer.status != this.ticketoffer.status){
            return false;   // not in the same state
        }

        if (ticketoffer.offer != this.offer){
            return false;   // is not the same offer
        }

        if (ticketoffer.__refndprice != this.__refndprice){
            return false;   // not the same refund price
        }

        if (ticketoffer.__fixedprice != this.__fixedprice){
            return false;   // not the same fixed price
        }

        return true;    // seems safe to be grouped (in the same item)
    }

    get Count(): number {
        return this.ticketoffer.amount || 0;
    }

    Merge(ticketoffer: TicketOfferView): void {
        if (!ticketoffer.amount){
            return;
        }

        // adjust the original asigned ticketoffer to the merged one
        const _ticketofferview = this.alives.get(this.ticketoffer) as TicketOfferView;
        if (_ticketofferview){
            for(const ticketproduct of ticketoffer.products){
                ticketproduct.offer = _ticketofferview;
            }    
        }

        // adjust the apliance amount for the destination ticketoffer
        this.ticketoffer.amount = this.Count + ticketoffer.amount;
    }

    /**********************************/
    /* PRICEINFO PRIVATE METHODS      */
    /**********************************/   

    private _ValidProducts(): Array <TicketProductView> {
        if (this._validproducts === null){
            const _products: Array <TicketProductView> = [];

            for(const _product of this.ticketoffer.products){
                if (_product.IsValid){
                    const _productview = this.alives.get(_product) as TicketProductView;
                    if (_productview){
                        _products.push(_productview);
                    }
                }
            }

            this._validproducts = _products;
        }

        return this._validproducts;
    }

     /* obtain the price of the offer + options, resolved by the ticket's place/table/zone/period */
     get __offerprice(): number | null {
        if (!this.offer){
            return null;
        }

        // the base price is the offer price
        let _offerprice = this.offer.price || 0;

        // add the price for the non included options
        for (const product of this.products){
            for (const option of product.ticketoptions){
                if (!this.AppliesTo(option)){
                    const _option = this.alives.get(option.option) as ProductOptView;
                    if (_option){
                        _offerprice += _option.tableprice(this.ticket?.place ?? null, this.ticket?.qrcode ?? null);
                    }
                }
            }
        }
        
        return _offerprice;
    }

    /* obtain the fixed for the offer */
    get __fixedprice(): number | null {
        if (this.ticketoffer.fixed){
            return this.ticketoffer.charge || 0;
        }

        return null;
    }

    /* obtain the original products price (all units linked to the offer) */
    get __totalprice(): number | null {
        let _totalprice = 0;

        for (const product of this._ValidProducts()){
            _totalprice += (product.__fixedprice ?? product.__prodtprice ?? 0) * (product.amount || 0);
        }

        return _totalprice;
    }

    /* obtain the final price for products still in the cart */
    get __finalprice(): number {
        let _finalprice = 0;

        for (const product of this.products){
            _finalprice += (product.calcinfo?.charge ?? product.charge ?? 0) * (product.amount || 0);
        }

        return Math.round(_finalprice * 100) / 100;
    }

    /* obtain the refund price for this ticketoffer */
    get __refndprice(): number | null {
        let _refndprice = 0;

        for (const product of this._ValidProducts()){
            if (product.status == 'DE' && (product.refund || 0) > 0){
                _refndprice += (product.refund || 0) * (product.amount || 0);
            }
        }

        return _refndprice;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TicketOfferView = ViewType <_TicketOfferView, TicketOffer>;

export function TicketOfferViewProxy(object: TicketOffer, data: dataService): TicketOfferView {
    return new _TicketOfferView(object, data).Proxy as TicketOfferView;
}
